// src/components/Stepper.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * Stepper Component
 * Displays progress through a series of steps using Font Awesome for icons.
 * Optimized for responsiveness on small devices.
 *
 * @param {object} props - Component props
 * @param {number} props.currentStep - The index of the currently active step (0-based).
 * @param {number} props.totalSteps - The total number of steps in the process.
 * @param {string[]} [props.stepLabels] - Optional array of labels for each step.
 * @param {string} [props.className] - Optional additional CSS classes for the container.
 */
const Stepper = ({ currentStep, totalSteps, stepLabels = [], className = '' }) => {
  // Validation
  if (totalSteps <= 0 || currentStep < 0 || currentStep >= totalSteps) {
    console.warn('Stepper: Invalid props provided (currentStep or totalSteps).');
    return null;
  }
  if (stepLabels.length > 0 && stepLabels.length !== totalSteps) {
    console.warn('Stepper: Mismatch between totalSteps and stepLabels length.');
  }

  // Create steps array
  const steps = Array.from({ length: totalSteps }, (_, i) => i);

  return (
    <div
      className={`flex items-start justify-between w-full max-w-full overflow-x-auto ${className}`}
      role="list"
      aria-label={`Registration progress: step ${currentStep + 1} of ${totalSteps}, ${stepLabels[currentStep] || `Step ${currentStep + 1}`}`}
    >
      {steps.map((stepIndex) => {
        const isActive = stepIndex === currentStep;
        const isCompleted = stepIndex < currentStep;
        const label = stepLabels[stepIndex] || `Step ${stepIndex + 1}`;

        return (
          <React.Fragment key={stepIndex}>
            {/* Step Indicator (Circle + Label) */}
            <div className="flex flex-col items-center text-center min-w-[32px] xs:min-w-[48px] sm:min-w-[56px] md:min-w-[80px]" role="listitem" aria-current={isActive ? 'step' : undefined}>
              <div
                className={`
                  w-6 h-6 xs:w-5 xs:h-5 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 mb-1
                  transition-colors duration-300 ease-in-out
                  ${isCompleted ? 'bg-primary border-primary text-secondary' : ''}
                  ${isActive ? 'border-primary text-primary bg-secondary' : 'border-neutral-light text-neutral-dark'}
                  ${!isCompleted && !isActive ? 'bg-white' : ''}
                `}
              >
                {isCompleted ? (
                  <FontAwesomeIcon icon={faCheck} className="text-white text-xs xs:text-sm sm:text-base" />
                ) : (
                  <span
                    className={`
                      font-semibold text-xs xs:text-sm sm:text-base
                      ${isActive ? 'text-primary' : 'text-neutral-dark'}
                    `}
                  >
                    {stepIndex + 1}
                  </span>
                )}
              </div>
              {/* Step Label */}
              <span
                className={`
                  text-[10px] xs:text-xs sm:text-sm px-1 max-w-[56px] xs:max-w-[64px] sm:max-w-[80px] md:max-w-[96px]
                  leading-tight break-words ${isActive ? 'text-primary font-medium' : 'text-neutral-dark'}
                `}
              >
                {label}
              </span>
            </div>

            {/* Connector Line (render between steps) */}
            {stepIndex < totalSteps - 1 && (
              <div
                className={`
                  h-1 mt-3 mx-1 xs:mx-1.5 sm:mx-2 rounded
                  transition-colors duration-300 ease-in-out
                  ${isCompleted ? 'bg-primary' : 'bg-neutral-light'}
                  w-6 xs:w-8 sm:w-10 md:flex-1
                `}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

Stepper.propTypes = {
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  stepLabels: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
};

export default Stepper;
